/**
 * Storage Service
 *
 * File management service with filesystem and S3 drivers.
 * Image processing (resize, cache) is handled by ImageProcessingService.
 */

import type { Logger } from 'pino'
import type { Knex } from 'knex'
import { basename, join } from 'node:path'
import { createHash } from 'node:crypto'
import { tmpdir } from 'node:os'
import { createWriteStream } from 'node:fs'
import { unlink } from 'node:fs/promises'
import { pipeline } from 'node:stream/promises'
import type { StorageDriver } from './drivers/driver.interface.js'
import { FilesystemDriver } from './drivers/filesystem.driver.js'
import { S3Driver } from './drivers/s3.driver.js'
import type { StorageConfig } from './storage.config.js'
import type { UploadedFile, UploadOptions, StorageFileRecord } from './storage.types.js'
import { getImageProcessingService } from './image-processing.service.js'
import { AppError, ErrorCodes } from '../../core/errors/app-error.js'
import type { LoggerReporter, ModuleContext } from '@gzl10/nexus-sdk'

const TABLE = 'storage_files'

/**
 * Sanitizes a filename to prevent path traversal and other attacks.
 * - Removes path traversal sequences (../, ..\)
 * - Removes null bytes
 * - Strips dangerous characters
 * - Falls back to 'file' if result is empty
 */
function sanitizeFilename(filename: string): string {
  // Get only the base name (removes directory parts)
  let safe = basename(filename)

  // Remove null bytes (can bypass extension checks)
  // eslint-disable-next-line no-control-regex
  safe = safe.replace(/\x00/g, '')

  // Remove path traversal attempts that might remain
  safe = safe.replace(/\.\./g, '')

  // Remove potentially dangerous characters for filesystems
  safe = safe.replace(/[<>:"|?*]/g, '')

  // Trim whitespace and dots from start/end
  safe = safe.replace(/^[\s.]+|[\s.]+$/g, '')

  // If empty after sanitization, use default name
  if (!safe || safe.length === 0) {
    safe = 'file'
  }

  return safe
}

/**
 * Resolved file for processing tools (ffmpeg, etc.).
 * Filesystem: direct path (zero-copy, no cleanup).
 * S3: streamed to temp file (cleanup removes temp).
 */
export interface ResolvedFile {
  /** Absolute path on disk */
  path: string
  /** Call when done — removes temp file (S3) or no-op (filesystem) */
  cleanup: () => Promise<void>
}

let storageServiceInstance: StorageService | null = null

/**
 * Gets the storage service instance
 */
export function getStorageService(): StorageService {
  if (!storageServiceInstance) {
    throw new Error('StorageService not initialized. Call initStorageService() first.')
  }
  return storageServiceInstance
}

/** Async function to resolve a StorageConfig by scope name */
export type ScopeConfigLookup = (scope: string) => Promise<StorageConfig | null>

export interface InitStorageServiceOptions {
  db: Knex
  config: StorageConfig
  logger: Logger
  errors: ModuleContext['core']['errors']
  nowTimestamp: (db: Knex) => string
  loggerService?: LoggerReporter
  getScopeConfig: ScopeConfigLookup
}

/**
 * Initializes the storage service
 */
export function initStorageService(options: InitStorageServiceOptions): StorageService {
  const { db, config, logger, errors, nowTimestamp, loggerService, getScopeConfig } = options
  storageServiceInstance = new StorageService(db, config, logger, errors, nowTimestamp, loggerService, getScopeConfig)
  return storageServiceInstance
}

export class StorageService {
  private driver: StorageDriver
  private config: StorageConfig
  private db: Knex
  private logger: Logger
  private errors: ModuleContext['core']['errors']
  private nowTimestamp: (db: Knex) => string
  private loggerService?: LoggerReporter
  private getScopeConfig: ScopeConfigLookup
  /** Driver cache by scope */
  private driverCache: Map<string, StorageDriver> = new Map()

  constructor(db: Knex, config: StorageConfig, logger: Logger, errors: ModuleContext['core']['errors'], nowTimestamp: (db: Knex) => string, loggerService: LoggerReporter | undefined, getScopeConfig: ScopeConfigLookup) {
    this.db = db
    this.config = config
    this.logger = logger.child({ service: 'storage' })
    this.errors = errors
    this.nowTimestamp = nowTimestamp
    this.loggerService = loggerService
    this.getScopeConfig = getScopeConfig

    // Inicializar driver por defecto según configuración
    this.driver = this.createDriver(config)
    this.logger.debug({ driver: config.driver }, 'Storage driver initialized')
  }

  /**
   * Creates a driver based on configuration
   */
  private createDriver(config: StorageConfig): StorageDriver {
    switch (config.driver) {
      case 's3':
        return new S3Driver(config)
      case 'filesystem':
      default:
        return new FilesystemDriver(config)
    }
  }

  /**
   * Gets a driver by scope (with cache)
   */
  private async getDriverByScope(scope: string): Promise<{ driver: StorageDriver; config: StorageConfig }> {
    // Obtener config via CollectionService lookup
    const config = await this.getScopeConfig(scope)
    if (!config) {
      throw new this.errors.NotFoundError(`Storage config for scope '${scope}'`)
    }

    // Revisar cache primero
    const cached = this.driverCache.get(scope)
    if (cached) {
      return { driver: cached, config }
    }

    // Crear nuevo driver
    const driver = this.createDriver(config)

    // Guardar en cache
    this.driverCache.set(scope, driver)
    this.logger.debug({ scope, driver: config.driver }, 'Driver created for scope')

    return { driver, config }
  }

  /**
   * Uploads a file
   */
  async upload(file: UploadedFile, options?: UploadOptions): Promise<StorageFileRecord> {
    // Obtener driver y config según scope (o usar defaults)
    let driver = this.driver
    let config = this.config

    if (options?.scope) {
      const scoped = await this.getDriverByScope(options.scope)
      driver = scoped.driver
      config = scoped.config
    }

    // Validar tamaño
    if (file.size > config.maxFileSize) {
      throw new this.errors.ValidationError(`File size ${file.size} exceeds limit ${config.maxFileSize}`)
    }

    // Validar tipo MIME
    if (config.allowedMimeTypes?.length) {
      const allowed = config.allowedMimeTypes.some(type => {
        if (type.endsWith('/*')) {
          return file.mimetype.startsWith(type.slice(0, -1))
        }
        return type === file.mimetype
      })
      if (!allowed) {
        throw new this.errors.ValidationError(`File type ${file.mimetype} not allowed`)
      }
    }

    // Sanitize filename to prevent path traversal and other attacks
    const safeFilename = sanitizeFilename(file.originalname)

    // Deduplication: check if file with same hash already exists
    if (options?.dedupe) {
      const hash = createHash('sha256').update(file.buffer).digest('hex')
      const existing = await this.db(TABLE)
        .select('*')
        .where({ hash })
        .first()

      if (existing) {
        this.logger.info({ id: existing.id, hash }, 'File deduplicated - using existing record')
        return this.mapRecord(existing)
      }
    }

    // Guardar archivo principal
    const storageFile = await this.safeDriverCall('put', safeFilename, () =>
      driver.put(file.buffer, safeFilename, {
        folder: options?.folder,
        mimetype: file.mimetype
      })
    )

    // Guardar metadatos en BD
    const now = this.nowTimestamp(this.db)

    // Always store relative URL (host-independent)
    const fileUrl = `/api/v1/storage/files/${storageFile.id}`

    const record: StorageFileRecord = {
      ...storageFile,
      url: fileUrl,
      created_at: now,
      updated_at: now,
      created_by: options?.userId || null,
      updated_by: options?.userId || null
    }

    // visibility defaults to 'internal' unless explicitly set
    const visibility = options?.visibility ?? 'internal'

    await this.db(TABLE).insert({
      id: record.id,
      filename: record.filename,
      disk_filename: record.diskFilename,
      mimetype: record.mimetype,
      size: record.size,
      folder: record.folder || null,
      scope: config.scope,
      path: record.path,
      url: record.url || null,
      hash: record.hash || null,
      visibility,
      metadata: null,
      created_at: now,
      updated_at: now,
      created_by: record.created_by,
      updated_by: record.updated_by
    })

    this.logger.info({ id: record.id, filename: record.filename, size: record.size }, 'File uploaded')

    return record
  }

  /**
   * Gets a file by ID
   */
  async getById(id: string): Promise<StorageFileRecord | null> {
    const record = await this.db(TABLE).where('id', id).first()
    if (!record) return null

    return this.mapRecord(record)
  }

  /**
   * Gets multiple files by IDs
   */
  async getByIds(ids: string[]): Promise<StorageFileRecord[]> {
    if (ids.length === 0) return []

    const records = await this.db(TABLE).whereIn('id', ids)
    return records.map((r: unknown) => this.mapRecord(r))
  }

  /**
   * Gets the appropriate driver for a scope.
   * If the scope matches the default, uses this.driver.
   */
  private async getDriverForScope(scope: string | undefined): Promise<StorageDriver> {
    // Si no hay scope o es el scope por defecto, usar driver default
    if (!scope || scope === this.config.scope) {
      return this.driver
    }
    return (await this.getDriverByScope(scope)).driver
  }

  /**
   * Gets a file stream
   */
  async getStream(id: string): Promise<NodeJS.ReadableStream> {
    const record = await this.getById(id)
    if (!record) {
      throw new this.errors.NotFoundError(`File not found: ${id}`)
    }

    const driver = await this.getDriverForScope(record.scope)
    return this.safeDriverCall('get', record.path, () => driver.get(record.path))
  }

  /**
   * Gets a file buffer
   */
  async getBuffer(id: string): Promise<Buffer> {
    const record = await this.getById(id)
    if (!record) {
      throw new this.errors.NotFoundError(`File not found: ${id}`)
    }

    const driver = await this.getDriverForScope(record.scope)
    return this.safeDriverCall('getBuffer', record.path, () => driver.getBuffer(record.path))
  }

  /**
   * Deletes a file record.
   * If other records reference the same physical file (by hash),
   * only removes the DB record but keeps the physical file.
   */
  async delete(id: string): Promise<void> {
    const record = await this.getById(id)
    if (!record) return

    // Delete the DB record first
    await this.db(TABLE).where('id', id).delete()

    // Check if other records share the same hash (deduplicated file)
    if (record.hash) {
      const otherRefs = await this.db(TABLE)
        .where('hash', record.hash)
        .count('* as count')
        .first<{ count: number | string }>()

      if (Number(otherRefs?.count) > 0) {
        this.logger.info(
          { id, filename: record.filename, hash: record.hash, otherRefs: otherRefs?.count },
          'File record removed (physical file preserved - other references exist)'
        )
        return
      }
    }

    // Invalidate resize cache before deleting physical file
    try {
      const imageService = getImageProcessingService()
      await imageService.invalidateCache(id)
    } catch {
      // ImageProcessingService may not be initialized in all contexts (e.g. tests)
    }

    // No other references, delete physical file
    const driver = await this.getDriverForScope(record.scope)
    await this.safeDriverCall('delete', record.path, () => driver.delete(record.path))

    this.logger.info({ id, filename: record.filename }, 'File deleted')
  }

  /**
   * Lists files with pagination
   */
  async findAll(params: {
    page: number
    limit: number
    folder?: string
    mimetype?: string
  }): Promise<{
    items: StorageFileRecord[]
    total: number
    page: number
    limit: number
    totalPages: number
    hasNext: boolean
  }> {
    const { page, limit, folder, mimetype } = params
    const offset = (page - 1) * limit

    let query = this.db(TABLE)

    if (folder) {
      query = query.where('folder', folder)
    }
    if (mimetype) {
      query = query.where('mimetype', 'like', `${mimetype}%`)
    }

    const [countResult, items] = await Promise.all([
      query.clone().count('* as count').first<{ count: string | number }>(),
      query.clone().orderBy('created_at', 'desc').limit(limit).offset(offset)
    ])

    const total = Number(countResult?.count ?? 0)
    const totalPages = Math.ceil(total / limit)

    return {
      items: items.map((r: unknown) => this.mapRecord(r)),
      total,
      page,
      limit,
      totalPages,
      hasNext: page < totalPages
    }
  }

  // === GDPR / Cross-module isolation methods ===

  /**
   * Delete all files created by a user (GDPR Art. 17)
   * Deletes DB records and physical files (respecting dedup)
   */
  async deleteUserFiles(userId: string): Promise<number> {
    const files = await this.db(TABLE)
      .select('id')
      .where({ created_by: userId })

    let count = 0
    for (const file of files) {
      await this.delete(file.id)
      count++
    }
    return count
  }

  /**
   * Get user file metadata for GDPR export (Art. 15)
   */
  async getUserFiles(userId: string): Promise<Array<{ id: string; filename: string; mimetype: string; size: number; created_at: string }>> {
    return this.db(TABLE)
      .select('id', 'filename', 'mimetype', 'size', 'created_at')
      .where({ created_by: userId })
      .orderBy('created_at', 'desc')
  }

  /**
   * Resolves a storage file to a local filesystem path for processing tools.
   *
   * - Filesystem driver: returns direct path (zero-copy).
   * - S3 driver: streams to temp file in os.tmpdir().
   *
   * Caller MUST call cleanup() in a finally block.
   */
  async resolveInputFile(id: string): Promise<ResolvedFile> {
    const record = await this.getById(id)
    if (!record) throw new this.errors.NotFoundError(`File not found: ${id}`)

    const driver = await this.getDriverForScope(record.scope)

    // Filesystem: direct path, no copy
    if (driver.getAbsolutePath) {
      const absPath = driver.getAbsolutePath(record.path)
      if (absPath) {
        return { path: absPath, cleanup: async () => {} }
      }
    }

    // S3 or any non-local driver: stream to temp file
    const tmpPath = join(tmpdir(), `nexus-video-${id}-${Date.now()}`)
    const stream = await this.safeDriverCall('get', record.path, () => driver.get(record.path))
    await pipeline(stream, createWriteStream(tmpPath))

    return {
      path: tmpPath,
      cleanup: async () => { await unlink(tmpPath).catch(() => {}) }
    }
  }

  /**
   * Gets a file stream with optional byte range (HTTP 206 Partial Content).
   */
  async getStreamRange(id: string, start?: number, end?: number): Promise<{
    record: StorageFileRecord
    stream: NodeJS.ReadableStream
    start: number
    end: number
    total: number
  }> {
    const record = await this.getById(id)
    if (!record) throw new this.errors.NotFoundError(`File not found: ${id}`)

    const total = record.size
    const resolvedStart = start ?? 0
    const resolvedEnd = end ?? (total - 1)

    const driver = await this.getDriverForScope(record.scope)
    const stream = await this.safeDriverCall('get', record.path, () =>
      driver.get(record.path, { start: resolvedStart, end: resolvedEnd })
    )

    return { record, stream, start: resolvedStart, end: resolvedEnd, total }
  }

  /**
   * Wraps a driver operation with error handling.
   * Logs the original error with context, rethrows as generic STORAGE_ERROR.
   * Never exposes driver internals (hostnames, regions, bucket names) to the client.
   */
  private async safeDriverCall<T>(operation: string, path: string, fn: () => Promise<T>): Promise<T> {
    try {
      return await fn()
    } catch (err) {
      // Already an AppError (e.g. NotFoundError from getById) — let it through
      if (err instanceof AppError) throw err

      this.logger.error({ err, operation, path: path.replace(/^.*\//, '…/') }, 'Storage driver error')
      throw new AppError({
        code: ErrorCodes.STORAGE_ERROR,
        message: 'Storage operation failed. Check storage configuration.'
      }, 500)
    }
  }

  /**
   * Maps a DB record to StorageFileRecord
   */
  private mapRecord(record: unknown): StorageFileRecord {
    const r = record as Record<string, unknown>

    return {
      id: r['id'] as string,
      filename: r['filename'] as string,
      diskFilename: r['disk_filename'] as string,
      mimetype: r['mimetype'] as string,
      size: r['size'] as number,
      folder: r['folder'] as string | undefined,
      scope: r['scope'] as string | undefined,
      path: r['path'] as string,
      url: r['url'] as string | undefined,
      hash: r['hash'] as string | undefined,
      created_at: r['created_at'] as string,
      updated_at: r['updated_at'] as string,
      created_by: r['created_by'] as string | null,
      updated_by: r['updated_by'] as string | null
    }
  }
}
